import type {
	AnchorHTMLAttributes,
	ElementType,
	LinkHTMLAttributes,
	ReactNode,
} from "react";
import { cn } from "./lib/utils";

export interface TextProps {
	children?: ReactNode;
	className?: string;
}

interface SubheadlineProps extends TextProps {
	level?: 1 | 2 | 3 | 4;
}

export function Headline({
	children,
	className,
}: TextProps): React.ReactElement {
	return (
		<h1 className={cn("text-2xl leading-[1.6rem]", className)}>{children}</h1>
	);
}

export function Subheadline({
	children,
	className,
}: SubheadlineProps): React.ReactElement {
	return (
		<h2
			className={cn(
				"text-xl leading-[1.24rem] font-medium",
				"mt-16 mb-8",
				className,
			)}
		>
			{children}
		</h2>
	);
}

export function MdHeadline({
	children,
	className,
	level = 2,
}: SubheadlineProps): React.ReactElement {
	const spacing = {
		1: "mt-20 mb-12",
		2: "mt-16 mb-8",
		3: "mt-12 mb-6",
		4: "mt-8 mb-4",
	}[level];

	return (
		<h2
			className={cn(
				"text-xl leading-[1.24rem] font-medium",
				spacing,
				className,
			)}
		>
			{children}
		</h2>
	);
}

export function Body({ children, className }: TextProps) {
	return (
		<p
			className={cn("mb-lg whitespace-pre-wrap break-words text-md", className)}
		>
			{children}
		</p>
	);
}

export function SimpleParagraph({ children, className }: TextProps) {
	return (
		<p className={cn("whitespace-pre-wrap break-words text-md", className)}>
			{children}
		</p>
	);
}

export function BodyAlt({ children, className }: TextProps) {
	return (
		<p
			className={cn(
				"whitespace-pre-wrap break-words text-text-body-muted text-xs",
				className,
			)}
		>
			{children}
		</p>
	);
}

export function SectionHeadline({
	children,
	className,
}: {
	children: React.ReactNode;
	className?: string;
}): React.ReactElement {
	return <h6 className={cn("text-lg", className)}>{children}</h6>;
}

export function MdList({
	children,
	className,
	ordered,
	...props
}: TextProps & { ordered?: boolean }): React.ReactElement {
	const Comp = ordered ? "ol" : "ul";

	return (
		<Comp
			className={cn(
				"mb-lg pl-lg text-md",
				Comp === "ul" ? "list-disc" : "list-decimal",
				className,
			)}
			{...props}
		>
			{children}
		</Comp>
	);
}

export function UnorderedList({
	children,
	className,
}: TextProps): React.ReactElement {
	return (
		<ul className={cn("mb-lg list-disc pl-lg text-md", className)}>
			{children}
		</ul>
	);
}

export function OrderedList({ children, className }: TextProps) {
	return (
		<ul className={cn("mb-lg list-disc pl-lg text-md", className)}>
			{children}
		</ul>
	);
}

export function LineItem({ children, className }: TextProps) {
	return <li className={cn("mb-sm", className)}>{children}</li>;
}

export function Label({
	children,
	className,
	ele = "label",
}: TextProps & { ele?: ElementType }): React.ReactElement {
	const Comp = ele;

	return (
		<Comp className={cn("text-sm text-text-label capitalize", className)}>
			{children}
		</Comp>
	);
}

export function Link({
	children,
	className,
	...props
}: TextProps &
	LinkHTMLAttributes<HTMLAnchorElement> &
	AnchorHTMLAttributes<HTMLAnchorElement>): React.ReactElement {
	return (
		<a
			className={cn("text-text-link hover:underline", className)}
			rel="noopener noreferrer"
			{...props}
		>
			{children}
		</a>
	);
}

export function Fineprint({
	children,
	className,
}: TextProps): React.ReactElement {
	return (
		<span className={cn("text-sm text-text-fineprint", className)}>
			{children}
		</span>
	);
}

export function Disclaimer({
	children,
	className,
}: TextProps): React.ReactElement {
	return (
		<span
			className={cn(
				"my-sm px-lg text-center text-sm text-text-fineprint",
				className,
			)}
		>
			{children}
		</span>
	);
}
